import React, { useState } from "react";
import styled from "styled-components";
import {
  Action,
  Body,
  FlexColumn,
  FlexRow,
  Title1,
  useChameleonTheme,
} from "@petsapp/chameleon";
import { ReactComponent as LogoSvg } from "../../components/logo.svg";
import breakpoint from "styled-components-breakpoint";
import { RegisterForm } from "./register-form";
import { LoginForm } from "./login-form";
import { useTranslation } from "../../utils/i18n";
import { ResetPasswordForm } from "./reset-password-form";
import { ResetSuccessful } from "./reset-successful";
import {
  useWellnessPlansBeforeAuth,
  WellnessPlanWithClinic,
} from "../../hooks/use-wellness-plans";
import { WellnessLandingSelectedPlan } from "../wellness-plans/wellness-landing-selected";
import { Spinner } from "../../components/loading";
import { useAnalytics } from "@petsapp/use-analytics";
import { EVENTS } from "./events";
import { WellnessLandingList } from "../wellness-plans/wellness-landing-list";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;

  ${breakpoint("tablet")`
    display: flex;
    flex-direction: row;
  `}
`;

const FormContainer = styled(FlexColumn)`
  align-items: center;
  overflow: scroll;

  ${breakpoint("tablet")`
    flex: 1;
    height: 100vh;
  `}
`;

const LogoBlock = styled(FlexRow)`
  background: ${(props) => props.theme.chameleon.color.Primary};
  justify-content: center;
  padding: 12px 0;

  ${breakpoint("tablet")`
    flex: 1;
    justify-content: center;
    display: block;
  `};
`;

const Logo = styled(LogoSvg)`
  height: 50px;

  path {
    fill: #fff;
  }

  ${breakpoint("tablet")`
    width: 25%;
    height: auto;
  `}
`;

const LinkButton = styled(Action).attrs(() => ({ as: "button" }))`
  background: transparent;
  padding: 0;
  border: none;
  cursor: pointer;
`;

export type FormType =
  | "login"
  | "register"
  | "reset-password"
  | "reset-successful";

export default function AuthController() {
  const [formType, setFormType] = useState<FormType>("login");
  const [
    wellnessPlan,
    setWellnessPlan,
  ] = useState<WellnessPlanWithClinic | null>(null);
  const [wellnessPlans, setWellnessPlans] = useState<
    WellnessPlanWithClinic[] | null
  >(null);
  const { t } = useTranslation();
  const { wrap } = useAnalytics();
  // @ts-ignore
  const { chameleon } = useChameleonTheme();
  const { loading } = useWellnessPlansBeforeAuth({
    onLoadWellnessPlan: (plans) => {
      if (plans !== null) {
        if (plans.wellnessPlan) {
          setWellnessPlan(plans.wellnessPlan);
        }
        if (plans.wellnessPlans) {
          if (plans.wellnessPlans.length === 1) {
            setWellnessPlan(plans.wellnessPlans[0]);
          } else {
            setWellnessPlans(plans.wellnessPlans);
          }
        }
        setFormType("register");
      }
    },
  });

  function toggle() {
    setFormType(formType === "login" ? "register" : "login");
  }

  if (loading) {
    return <Spinner />;
  }

  const copy: {
    [key in FormType]: { title: string; subtitle?: string; link?: string };
  } = {
    login: {
      title: t("Sign in"),
      subtitle: t("Don't have an account?"),
      link: t("Create account"),
    },
    register: {
      title: wellnessPlan
        ? `Join ${wellnessPlan.label}’s Pet Health Plan`
        : Array.isArray(wellnessPlans) && wellnessPlans[0]
        ? `Join ${wellnessPlans[0].label}’s Pet Health Plan`
        : t("Create your account"),
      subtitle: t("Already have an account?"),
      link: t("Sign in"),
    },
    "reset-password": {
      title: t("Reset your password"),
      subtitle: t("Already have an account?"),
      link: t("Sign in"),
    },
    "reset-successful": {
      subtitle: t("We've sent you an email with a link to reset your password"),
      title: t("Password reset"),
    },
  };

  return (
    <PageContainer>
      <FormContainer>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            padding: "12vh 16px",
          }}
        >
          <FlexColumn
            style={{
              width: "100%",
              maxWidth: 320,
            }}
          >
            <Title1>{copy[formType].title}</Title1>
            <div style={{ marginBottom: 24 }}>
              {copy[formType].subtitle && (
                <>
                  <Body as="span" style={{ marginRight: 2 }}>
                    {copy[formType].subtitle!}
                  </Body>{" "}
                </>
              )}
              {copy[formType].link && (
                <LinkButton
                  onClick={wrap(
                    toggle,
                    formType === "register"
                      ? EVENTS.alreadyRegistered
                      : EVENTS.showCreateAccount,
                    { isWellnessPlanSignup: Boolean(wellnessPlan) },
                  )}
                  textColor={chameleon.color.Primary}
                >
                  {copy[formType].link!}
                </LinkButton>
              )}
            </div>
            <div style={{ width: "100%" }}>
              {formType === "login" && <LoginForm setFormType={setFormType} />}
              {formType === "register" && <RegisterForm plan={wellnessPlan} />}
              {formType === "reset-password" && (
                <ResetPasswordForm setFormType={setFormType} />
              )}
              {formType === "reset-successful" && (
                <ResetSuccessful setFormType={setFormType} />
              )}
            </div>
          </FlexColumn>
        </div>
      </FormContainer>
      <LogoBlock>
        <FlexRow
          style={{
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {wellnessPlan ? (
            <WellnessLandingSelectedPlan plan={wellnessPlan} />
          ) : wellnessPlans ? (
            <WellnessLandingList plans={wellnessPlans} />
          ) : (
            <Logo />
          )}
        </FlexRow>
      </LogoBlock>
    </PageContainer>
  );
}
